import * as React from 'react';
import { useEffect, useState } from 'react';
import { Survey, LanguageCode, LanguageCodes, basicValidation, getSurveyStatus } from '../../../amorg-common';
import { useParams } from 'react-router-dom';
import { getSurveyAsAnonymous, startAnswer } from '../../../api/surveySubmissionClientApi';
import { stxt } from '../../../SurveyTxt';
import { getBrowserLanguage } from '../../../utils/getBrowserLanguage';
import { parseSiteScopedRouteParam } from '../../../utils/parseSiteScopedRouteParam';
import { SurveyAnswerPageWrapper } from './_components/SurveyAnswerPageWrapper';
import { Button } from '../../../designSystem/button/Button';
import { TextField } from '../../../designSystem/formField/text/TextField';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { SurveyInnerCard, SurveyOuterCard } from './_components/SurveyCard';
import { H2 } from '../../../designSystem/typography/Heading';
import { SurveyFactBox } from './_components/SurveyFactBox';
import { SurveyStyledList } from './_components/SurveyStiledList';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { Icon } from '../../../designSystem/icons/Icons';
import { colors } from '../../../designSystem/colors/color';
import { P } from '../../../designSystem/typography/Body';


export function StartSurveyAnswerPage(): JSX.Element 
{
    const params = useParams<{ surveyIdSiteId:string }>();
    const surveyRouteParams = parseSiteScopedRouteParam(params.surveyIdSiteId);

    return (
        <StartSurveyAnswerPageContent siteId={surveyRouteParams.siteId} surveyId={surveyRouteParams.value} />
    );
}


type PageState = {
    loadStatus:LoadStatus,
    survey:Survey | null,
    email:string,
};

function StartSurveyAnswerPageContent({ siteId, surveyId }: { siteId: string, surveyId: string }): JSX.Element 
{
    const [state, setState] = useState<PageState>({
        loadStatus: 'loading',
        survey:null,
        email:''
    });
    
    const [selectedLanguage,setSelectedLanguage] = useState<LanguageCode>(getBrowserLanguage());

    useEffect(() => {
        const getData = async () => {
            const survey = await getSurveyAsAnonymous(siteId, surveyId);
            setState({
                ...state,
                survey,
                loadStatus: survey ? 'done' : 'error'
            });
        };
        getData();
    }, []);

    const availableLanguages = LanguageCodes.map(lc => lc);

    return (
        <SurveyAnswerPageWrapper 
            siteId={siteId}
            availableLanguages={availableLanguages}
            selectedLanguage={selectedLanguage}
            onSelectedLanguageChange={(languageCode) => setSelectedLanguage(languageCode)}>
            
            <StartAnswer
                {...state}
                siteId={siteId}
                selectedLanguage={selectedLanguage}
                onEmailChange={(val)=> setState({...state, email:val}) }
            />

        </SurveyAnswerPageWrapper>
    );
}

export type LoadStatus = 'loading' | 'done' | 'error';
type SubmissionResult = 'pending' | 'success' | 'failure';

export type StartAnswerProps = {
    loadStatus: LoadStatus,
    siteId: string,
    survey: Survey | null,
    email: string,
    selectedLanguage:LanguageCode,
    onEmailChange: (val: string) => void
}
export function StartAnswer(props: StartAnswerProps): JSX.Element {
    const [didSubmit, setDidSubmit] = React.useState(false);
    const [submissionResult, setSubmissionResult] = React.useState<SubmissionResult>('pending');

    async function handleClickSubmit() {
        if(!props.survey) 
            return;
        
        const result = await startAnswer(props.siteId, props.survey.id, props.email);
        setSubmissionResult(result.isSuccess ? 'success' : 'failure');
        setDidSubmit(true);
    }

    const txt = stxt(props.selectedLanguage);

    return (
        <SurveyOuterCard>

            {props.loadStatus === 'loading' &&
                <LoadingPageOverlay loading={true} />
            }

            <Flex flexDirection="column" gap={32} style={{maxWidth: '600px', margin: '0 auto'}}>

                {props.loadStatus === 'error' &&
                    <Flex gap={16} alignItems="center">
                        <Icon name="status_error" color={colors.icon.error} size="medium" />
                        <div>{txt.startAnswer_error}</div>
                    </Flex>
                }

                {props.loadStatus === 'done' && props.survey && !getSurveyStatus(props.survey).canSubmitAnswer &&
                
                    <Flex gap={16} alignItems="center">
                        <Icon name="clock" color={colors.icon.error} size="medium" />
                        <div>{txt.startAnswer_surveyClosed}</div>
                    </Flex>
                }

                {props.loadStatus === 'done' && props.survey && getSurveyStatus(props.survey).canSubmitAnswer &&

                    <>
                        <Flex justifyContent='center'>
                            <Icon name="bar_chart" size="large" color={colors.icon.active} />
                        </Flex>

                        <Flex flexDirection="column" gap={8}>
                            <H2>{txt.startAnswer_header}</H2>
                            <P>{txt.startAnswer_intro}</P>
                        </Flex>

                        {submissionResult === 'failure' &&
                            <SurveyInnerCard>
                                <Flex gap={16} alignItems="center">
                                    <Icon name="status_error" color={colors.icon.error} size="medium" />
                                    <div>{txt.startAnswer_submissionError}</div>
                                </Flex>
                            </SurveyInnerCard>
                        }

                        {submissionResult === 'success' &&
                            <SurveyInnerCard>
                                <Flex gap={16} alignItems="center">
                                    <Icon name="status_ok" color={colors.icon.success} size="medium" />
                                    <div dangerouslySetInnerHTML={{ __html: txt.startAnswer_submissionSuccess }} />
                                </Flex>
                            </SurveyInnerCard>
                        }
                        
                        {submissionResult !== 'success' &&
                            <Flex flexDirection='column' gap={8}>
                                <TextField
                                    label="Email"
                                    value={props.email}
                                    onChange={props.onEmailChange}
                                    validator={value => !didSubmit || basicValidation.isValidEmail(value)
                                        ? {valid:true}
                                        : {valid:false, errorMessage:txt.startAnswer_invalidEmailError}
                                    } />    
                                
                                <Button
                                    variant="filled"
                                    onClick={handleClickSubmit}
                                    text={txt.startAnswer_buttonRequestLink} 
                                    size={"42px"} />
                            </Flex>
                        }

                        <SurveyFactBox
                            icon="domino_mask"
                            header2={txt.startAnswer_anonymityHeader}
                            content={txt.startAnswer_anonymityText}
                        />

                        <div>
                            <H2>Sådan fungerer det</H2>
                            <SurveyStyledList
                                items={[
                                    { 
                                        header: 'Indtast din e-mail',
                                        text: 'Tryk på knappen for at anmode om dit personlige link til undersøgelsen.'
                                    },
                                    {
                                        header: 'Modtag link',
                                        text: 'I indbakken på under 1 minut vil du modtage dit personlige link til undersøgelsen.'
                                    },
                                    {
                                        header: 'Start din besvarelse',
                                        text: 'Besvar undersøgelsen når det passer dig. Hvis du lukker browseren undervejs, kan du genoptage besvarelsen via dit personlige link når det passer dig.'
                                    }
                                ]}
                            />
                        </div>
                    </>
                }
            </Flex>
        </SurveyOuterCard>
    );
}